import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "@/utils/axiosInstance";
import { handleThunkError } from "@/utils/thunkErrorHandler";
import { Params } from "@/types";
import type {
  AsaasSubaccountCreatePayload,
  AsaasSubaccountUpdatePayload,
  AsaasOwnershipTransferPayload,
} from "@/types";

// Base endpoints (admin scope)
const endpoint = {
  overview: "/admin/asaas/subaccounts/overview",
  subaccount: (establishmentId: number | string) =>
    `/admin/asaas/subaccount/${establishmentId}`,
  create: "/admin/asaas/subaccount",
  transfer: (establishmentId: number | string) =>
    `/admin/asaas/subaccount/${establishmentId}/transfer-ownership`,
  remove: (establishmentId: number | string) =>
    `/admin/asaas/subaccount/${establishmentId}`,
  webhooks: (establishmentId: number | string) =>
    `/admin/asaas/webhooks/${establishmentId}`,
  activateWebhook: (establishmentId: number | string) =>
    `/admin/asaas/webhooks/${establishmentId}/activate`,
  deleteWebhook: (
    establishmentId: number | string,
    webhookId: string | number,
  ) => `/admin/asaas/webhooks/${establishmentId}/${webhookId}`,
};

export interface OverviewQueryParams extends Partial<Params> {
  search?: string;
  is_active?: boolean | string;
  plan_id?: number | string | null;
  has_subaccount?: boolean | string;
  date_from?: string;
  date_to?: string;
  min_subscriptions?: number;
  max_subscriptions?: number;
  sort?: string;
  direction?: "asc" | "desc";
  per_page?: number;
  page?: number;
}

export const fetchAsaasSubaccountsOverview = createAsyncThunk(
  "asaasAdmin/fetchOverview",
  async (query: OverviewQueryParams, thunkAPI) => {
    try {
      // Debug: log query being used
      // eslint-disable-next-line no-console
      console.debug(
        "thunks: fetchAsaasSubaccountsOverview ->",
        endpoint.overview,
        query,
      );
      const response = await axiosInstance.get(endpoint.overview, {
        params: query,
      });
      // eslint-disable-next-line no-console
      console.debug(
        "thunks: fetchAsaasSubaccountsOverview response ->",
        response && response.data,
      );

      return response.data;
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Erro ao carregar overview de subcontas",
      );
    }
  },
);

export const createAsaasSubaccount = createAsyncThunk(
  "asaasAdmin/createSubaccount",
  async (payload: AsaasSubaccountCreatePayload, thunkAPI) => {
    try {
      const response = await axiosInstance.post(endpoint.create, payload);
      return response.data;
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao criar subconta");
    }
  },
);

export const fetchAsaasSubaccountDetail = createAsyncThunk(
  "asaasAdmin/fetchSubaccountDetail",
  async (establishmentId: number | string, thunkAPI) => {
    try {
      const response = await axiosInstance.get(
        endpoint.subaccount(establishmentId),
      );
      return { establishmentId, ...response.data };
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao carregar subconta");
    }
  },
);

export const updateAsaasSubaccount = createAsyncThunk(
  "asaasAdmin/updateSubaccount",
  async (
    {
      establishmentId,
      data,
    }: {
      establishmentId: number | string;
      data: AsaasSubaccountUpdatePayload;
    },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.put(
        endpoint.subaccount(establishmentId),
        data,
      );
      return { establishmentId, ...response.data };
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao atualizar subconta");
    }
  },
);

export const transferAsaasOwnership = createAsyncThunk(
  "asaasAdmin/transferOwnership",
  async (
    {
      establishmentId,
      data,
    }: {
      establishmentId: number | string;
      data: AsaasOwnershipTransferPayload;
    },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.patch(
        endpoint.transfer(establishmentId),
        data,
      );
      return { establishmentId, ...response.data };
    } catch (error) {
      return handleThunkError(
        error,
        thunkAPI,
        "Erro na transferência de titularidade",
      );
    }
  },
);

export const deleteAsaasSubaccount = createAsyncThunk(
  "asaasAdmin/deleteSubaccount",
  async (establishmentId: number | string, thunkAPI) => {
    try {
      const response = await axiosInstance.delete(
        endpoint.remove(establishmentId),
      );
      return { establishmentId, ...response.data };
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao remover subconta");
    }
  },
);

export const fetchAsaasWebhooks = createAsyncThunk(
  "asaasAdmin/fetchWebhooks",
  async (establishmentId: number | string, thunkAPI) => {
    try {
      const response = await axiosInstance.get(
        endpoint.webhooks(establishmentId),
      );
      return { establishmentId, ...response.data };
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao listar webhooks");
    }
  },
);

export const activateAsaasWebhook = createAsyncThunk(
  "asaasAdmin/activateWebhook",
  async (
    {
      establishmentId,
      url,
    }: { establishmentId: number | string; url?: string },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.post(
        endpoint.activateWebhook(establishmentId),
        url ? { url } : {},
      );
      return { establishmentId, ...response.data };
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao ativar webhook");
    }
  },
);

export const deleteAsaasWebhook = createAsyncThunk(
  "asaasAdmin/deleteWebhook",
  async (
    {
      establishmentId,
      webhookId,
    }: { establishmentId: number | string; webhookId: string | number },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.delete(
        endpoint.deleteWebhook(establishmentId, webhookId),
      );
      return { establishmentId, webhookId, ...response.data };
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao remover webhook");
    }
  },
);
